import React from 'react';
import {
  AppMode,
  AvailabilityStatus,
  AvatarConfig,
  ChatWidget,
  expandCollapseManager,
  FileSelectionMethods,
  ISendersData,
} from '@wix/chat-web';
import serverApi from '../../../services/serverApi';
import wix from '../../../services/wix';
import firebaseChatService from '../../../services/firebaseChatService';
import { EditorViewMode } from '../../../constants/editor-view-mode';
import * as biLogger from '../../../services/biLogger';
import { mediaManager, ON_NEW_MESSAGE } from '@wix/inbox-common';
import withMobile from '../../../hoc/mobile';
import { withExperiments } from '@wix/wix-experiments-react';
import { CTA_PRODUCT } from '../../../constants/call-to-action';
import {
  editorSettingsAvatar,
  getLocalizedTexts,
  isMessageNotificationEnabled,
  primaryColorOrDefault,
  theme,
} from '../../../reducers';
import { connect } from 'react-redux';
import _get from 'lodash/get';
import {
  collapseWidget,
  expandWidget,
  firstExpand,
  goOffline,
  updateHasMessages,
} from '../../../actions';
import { initApp } from '../../../actions/serverApi';
import { DeliveryMethodId, Message } from '@wix/chat-sdk';
import { LazyChatRoomScreen } from './LazyChatRoomScreen';
import {
  lazyLoaders,
  loadingScheduler,
} from '../../../services/loading-scheduler';
import { MessagePopup } from '../../MessagePopup/MessagePopup';
import { ViewerOnMessageSentEvent } from '../../../constants/viewer-event-types';
import * as Sentry from '@sentry/browser';
import { WidgetSenderIdentityType } from '../../../constants/widget-sender-identity';
import { Experiments } from '../../../constants/experiments';

interface BusinessOnlyWidgetProps {
  visibilitySettings: any;
  themeData: any;
  viewMode: EditorViewMode;
  locale: string;
  experiments: any;
  onMessageReceived: Function;
  editorSettingsLayout: any;
  localizedTexts: any;
  editorSettingsBehaviour: any;
  editorSettingsAvatar: any;
  firebaseGoOffline: Function;
  initApp: Function;
  shardId: string;
  businessChatRoomId: string;
  isChatSdkInit: boolean;
  deviceType: string;
  availabilityStatus: AvailabilityStatus;
  isBranded: boolean;
  widgetExpanded: boolean;
  expandWidget: Function;
  collapseWidget: Function;
  hasMessages: boolean;
  updateHasMessages: Function;
  widgetMode: string;
  firstExpand: Function;
  isContact: boolean;
  unreadCount: number;
  isMessageNotificationEnabled: boolean;
  chatOnPage: boolean;
  host: string;
  primaryColor: string;
  visitorId: string;
  widgetSenderIdentity: string;
  widgetSenderData: ISendersData;
  isResponsive: boolean;
}

interface BusinessOnlyWidgetState {
  instanceId: string;
  alreadySentAwayMessage: boolean;
  alreadySentLcf: boolean;
  isStateNotificationEnabled: boolean;
}

class BusinessOnlyWidget extends React.Component<
  BusinessOnlyWidgetProps,
  BusinessOnlyWidgetState
> {
  constructor(props: BusinessOnlyWidgetProps) {
    super(props);
    this.state = {
      instanceId: wix.getInstanceId(),
      alreadySentAwayMessage: false,
      alreadySentLcf: false,
      isStateNotificationEnabled: this.props.isMessageNotificationEnabled,
    };
  }

  shouldShowChat = (chatOnPage: boolean) => {
    const {
      visibilitySettings: { publicVisibility, chatOnboarded, editorVisibility },
    } = this.props;

    return this.isInSiteView()
      ? chatOnPage && publicVisibility && chatOnboarded
      : editorVisibility;
  };

  async componentDidMount() {
    Sentry.addBreadcrumb({
      category: 'MountedWidget',
      message: 'BusinessOnly',
    });
    expandCollapseManager.registerOnExpand(this.handleExpand);
    expandCollapseManager.registerOnCollapse(this.handleCollapse);

    this.showOrHideChat();
    await this.props.initApp({});

    void loadingScheduler.onInteractive().then(() => {
      if (this.props.hasMessages) {
        this.onMessageReceived();
      }
    });

    wix
      .isResponsive()
      .then((isResponsive) => {
        if (isResponsive && this.isMobile()) {
          this.setState({ isStateNotificationEnabled: true });
        }
      })
      .catch(() => {});
  }

  componentWillUnmount() {
    expandCollapseManager.unregisterOnExpand(this.handleExpand);
    expandCollapseManager.unregisterOnCollapse(this.handleCollapse);
    this.props.firebaseGoOffline();
    this.props.updateHasMessages(false);
  }

  showOrHideChat() {
    if (!this.shouldShowChat(this.props.chatOnPage)) {
      void this.hide();
    }
  }

  isInSiteView = () => this.props.viewMode === EditorViewMode.SITE;

  isMobile = () => this.props.deviceType === 'mobile';

  handleExpand = async ({ direction = 'uou', referrer = 'widget' } = {}) => {
    const { host } = this.props;
    biLogger.expandChatEvent({ direction, referrer, host });
    await this.props.expandWidget();

    this.onPostExpand();
  };

  onPostExpand() {
    this.props.firstExpand();
    void this.handleSendAwayMessage();
  }

  async handleSendAwayMessage() {
    if (
      !this.state.alreadySentAwayMessage &&
      this.props.availabilityStatus === AvailabilityStatus.Away &&
      !this.props.hasMessages &&
      this.props.isContact
    ) {
      const {
        localizedTexts: { offlineChatMessage },
      } = this.props;

      const messageDto = Message.fromText({
        text: offlineChatMessage,
        chatroomId: this.props.businessChatRoomId,
        userId: 'business',
        summary: '',
      });

      const lazySdk = await lazyLoaders.interactivity.chatSdk();
      void lazySdk.chatSdk.addMessage(messageDto);
    }

    this.setState({ alreadySentAwayMessage: true });
  }

  handleCollapse = async () => {
    const thereIsQAB = this.isMobile() && !this.props.isResponsive;
    if (!thereIsQAB) {
      return this.props.collapseWidget();
    }
  };

  triggerCollapse = () => expandCollapseManager.collapse();

  hide() {
    return wix.resizeToHidden();
  }

  handleOnClick = () => {
    if (this.isInSiteView()) {
      void loadingScheduler.onConnective().then(() => {
        firebaseChatService.goOnlineIfOffline();
      });
    }
  };

  onMessageReceived = () => {
    this.props.updateHasMessages(true);

    biLogger.messageReceivedEvent(this.props.shardId);

    const chatSetNewMsgToExpand =
      this.props.editorSettingsBehaviour.onNewMessage !==
      ON_NEW_MESSAGE.SHOW_NOTIFICATION;
    const widgetCollapsed = !this.props.widgetExpanded;

    if (this.isMobile()) {
      this.props.onMessageReceived();
    } else if (widgetCollapsed && chatSetNewMsgToExpand) {
      void this.handleExpand({ direction: 'user', referrer: 'widget' });
    }
  };

  onSendMessage = (message) => {
    const { host } = this.props;
    wix.publishToPubSub(ViewerOnMessageSentEvent, message);
    return biLogger.sendMessageEvent({ host });
  };
  onAttachmentClick = () => biLogger.attachmentClickEvent();
  onCallToActionClick = (url) => {
    biLogger.callToActionClickEvent(url);
    this.openProductPage(url);
  };

  openProductPage = (url) => {
    this.isMobile() && this.triggerCollapse();
    const productRegex = /product-page\/([^?/\s]+)/gm;
    const productMatch = productRegex.exec(url);
    const fallbackBehavior = () => window.open(url);
    if (productMatch) {
      const productName = productMatch[1];
      wix.navigateToSection(
        {
          appDefinitionId: CTA_PRODUCT.APP_DEFINITION_ID,
          sectionId: CTA_PRODUCT.SECTION_ID,
        },
        productName,
        fallbackBehavior,
      );
    } else {
      fallbackBehavior();
    }
  };

  onChatDisplayed = () => {
    if (this.shouldShowChat(true)) {
      const asRounded = _get(
        this.props.editorSettingsLayout,
        'collapsedAsRounded',
        false,
      );
      const layoutName = asRounded
        ? 'collapsedAsRounded'
        : 'selectedRadiusOption';

      biLogger.chatDisplayedEvent(this.props.deviceType, layoutName);
    }
  };

  handleUploadFile = async (file: File, source: FileSelectionMethods) => {
    const { token, url, folderId } = await serverApi.getUploadToken();
    const isNewGen = this.isNewGenMediaEnabled();
    const effectiveFolderId = isNewGen ? folderId : undefined;

    if (source !== FileSelectionMethods.Input) {
      const { businessChatRoomId, visitorId } = this.props;
      biLogger.fileUploadFromClipboard(
        file.name,
        file.size,
        file.type,
        '',
        businessChatRoomId,
        visitorId,
        source === FileSelectionMethods.DragNDrop,
      );
    }

    return mediaManager.uploadFileToMedia({
      file,
      token,
      uploadUrlApi: url,
      folderId: effectiveFolderId,
    });
  };

  isNewGenMediaEnabled = () => {
    return (
      this.props.experiments.enabled('specs.media.MediaManager3') &&
      this.props.experiments.enabled('specs.crm.WixChatUsingMM3Spec')
    );
  };

  eventHooks = {
    onSendMessage: (message) => this.onSendMessage(message),
    onMessageReceived: () => this.onMessageReceived(),
    onAttachmentClick: () => this.onAttachmentClick(),
    onChatDisplayed: () => this.onChatDisplayed(),
    onCallToActionClick: (url) => this.onCallToActionClick(url),
  };

  onMessageNotificationClick = () => {
    expandCollapseManager.expand({
      direction: 'uou',
      referrer: 'message indication',
    });
  };

  render() {
    const { instanceId } = this.state;
    const {
      themeData,
      locale,
      visibilitySettings,
      isChatSdkInit,
      businessChatRoomId,
      availabilityStatus,
      isBranded,
      widgetExpanded,
      widgetMode,
      unreadCount,
      chatOnPage,
      widgetSenderIdentity,
      widgetSenderData,
      isResponsive,
      experiments,
    } = this.props;
    const {
      localizedTexts: {
        onlineTitle,
        onlineMessage,
        offlineTitle,
        offlineMessage,
      },
      editorSettingsBehaviour: { responseTimeEnabled, responseTimeSelected },
    } = this.props;
    const avatar = this.props.editorSettingsAvatar;
    const dummyMode = this.isInSiteView() && !visibilitySettings.chatOnboarded;
    const isMobile = this.isMobile();
    const isAvailable = availabilityStatus !== AvailabilityStatus.Away;
    const typicallyReplies = {
      isEnabled: responseTimeEnabled,
      period: responseTimeSelected,
    };
    const shouldRender = isChatSdkInit || widgetMode === 'site';
    const messageDisplayOption =
      widgetSenderIdentity === WidgetSenderIdentityType.ANONYMOUS
        ? {
            avatarOption: AvatarConfig.NONE,
            titleOption: AvatarConfig.NONE,
          }
        : {
            avatarOption: AvatarConfig.INCOMING,
            titleOption: AvatarConfig.INCOMING,
          };

    const deliveryMethods = experiments.enabled(Experiments.ChatBadgeFromWidget)
      ? [DeliveryMethodId.Chat]
      : undefined;

    return shouldRender ? (
      <div onClick={this.handleOnClick}>
        {this.state.isStateNotificationEnabled && (
          <MessagePopup
            onClick={this.onMessageNotificationClick}
            activeRoom={businessChatRoomId}
            isResponsive={isResponsive}
          />
        )}
        <ChatWidget
          isExpanded={(isMobile && !isResponsive) || widgetExpanded}
          theme={themeData}
          onlineTitle={isAvailable ? onlineTitle : offlineTitle}
          onlineMessage={isAvailable ? onlineMessage : offlineMessage}
          avatar={avatar}
          eventHooks={this.eventHooks}
          available={this.shouldShowChat(chatOnPage)}
          isMobile={isMobile}
          isResponsive={isResponsive}
          locale={locale}
          availabilityStatus={availabilityStatus || undefined}
          unreadCount={unreadCount}
          typicallyReplies={typicallyReplies}
        >
          <LazyChatRoomScreen
            chatroomId={businessChatRoomId}
            dummyMode={dummyMode}
            onUploadFile={this.handleUploadFile}
            instanceId={instanceId}
            banner={isBranded ? true : undefined}
            unfoldWhenEmpty={isAvailable}
            appMode={AppMode.Widget}
            locale={locale}
            theme={themeData}
            messageDisplayOption={messageDisplayOption}
            sendersData={widgetSenderData}
            deliveryMethods={deliveryMethods}
          />
        </ChatWidget>
      </div>
    ) : null;
  }
}

function mapStateToProps(state) {
  return {
    themeData: theme(state),
    editorSettingsAvatar: editorSettingsAvatar(state),
    localizedTexts: getLocalizedTexts(state),
    editorSettingsBehaviour: state.editorSettingsBehaviour,
    editorSettingsLayout: state.editorSettingsLayout,
    visibilitySettings: state.visibilitySettings,
    viewMode: state.viewMode,
    locale: state.locale,
    shardId: state.shardId,
    businessChatRoomId: state.businessChatRoomId,
    isChatSdkInit: state.isChatSdkInit,
    deviceType: state.deviceType,
    availabilityStatus: state.availabilityStatus,
    isBranded: state.isBranded,
    widgetExpanded: state.widgetExpanded,
    isContact: state.isLcfMandatory.isContact,
    hasMessages: state.hasMessages,
    widgetMode: state.widgetMode,
    unreadCount: state.unreadCount,
    isMessageNotificationEnabled: isMessageNotificationEnabled(state),
    chatOnPage: state.chatOnPage,
    host: state.props,
    primaryColor: primaryColorOrDefault(state),
    visitorId: state.chatInitResult?.visitorId,
    widgetSenderIdentity: state.widgetSenderIdentity,
    widgetSenderData: state.widgetSenderData,
  };
}

const mapDispatchToProps = (dispatch) => ({
  firebaseGoOffline: () => dispatch(goOffline()),
  initApp: (data) => dispatch(initApp(data)),
  expandWidget: () => dispatch(expandWidget()),
  collapseWidget: () => dispatch(collapseWidget()),
  firstExpand: () => dispatch(firstExpand()),
  updateHasMessages: (hasMessages) => dispatch(updateHasMessages(hasMessages)),
});

export default connect(
  mapStateToProps,
  mapDispatchToProps,
)(withExperiments(withMobile(BusinessOnlyWidget)));
